const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

const encode = (text) => new TextEncoder().encode(text);

function dosStamp(date) {
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | (Math.floor(date.getSeconds() / 2) & 0x1f),
    date: (((date.getFullYear() - 1980) & 0x7f) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

function zip(files) {
  const { time, date } = dosStamp(new Date());
  const parts = [];
  const directory = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = encode(file.name);
    const crc = crc32(file.data);

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(8, 0, true);
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, file.data.length, true);
    lv.setUint32(22, file.data.length, true);
    lv.setUint16(26, nameBytes.length, true);
    local.set(nameBytes, 30);

    const entry = new Uint8Array(46 + nameBytes.length);
    const ev = new DataView(entry.buffer);
    ev.setUint32(0, 0x02014b50, true);
    ev.setUint16(4, 20, true);
    ev.setUint16(6, 20, true);
    ev.setUint16(10, 0, true);
    ev.setUint16(12, time, true);
    ev.setUint16(14, date, true);
    ev.setUint32(16, crc, true);
    ev.setUint32(20, file.data.length, true);
    ev.setUint32(24, file.data.length, true);
    ev.setUint16(28, nameBytes.length, true);
    ev.setUint32(42, offset, true);
    entry.set(nameBytes, 46);

    parts.push(local, file.data);
    directory.push(entry);
    offset += local.length + file.data.length;
  });

  const directorySize = directory.reduce((sum, d) => sum + d.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, files.length, true);
  endView.setUint16(10, files.length, true);
  endView.setUint32(12, directorySize, true);
  endView.setUint32(16, offset, true);

  const all = [...parts, ...directory, end];
  const total = all.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  all.forEach((chunk) => { out.set(chunk, at); at += chunk.length; });
  return out;
}

const escapeXml = (value) =>
  String(value)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&apos;")
    // eslint-disable-next-line no-control-regex
    .replace(/[ --]/g, "");

function columnLetter(index) {
  let n = index + 1;
  let letters = "";
  while (n > 0) {
    const rest = (n - 1) % 26;
    letters = String.fromCharCode(65 + rest) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

const NUMBER_TEXT = /^-?(0|[1-9]\d{0,2}(,\d{3})+|[1-9]\d*)(\.\d+)?$/;
const isNumeric = (value) =>
  typeof value === "number"
    ? Number.isFinite(value)
    : typeof value === "string" && NUMBER_TEXT.test(value.trim()) && value.trim().length <= 15;

function cellXml(ref, value, styleId) {
  if (value === null || value === undefined || value === "" || value === "—") {
    return `<c r="${ref}" s="${styleId}"/>`;
  }
  if (isNumeric(value)) {
    const num = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
    return `<c r="${ref}" s="${styleId}"><v>${num}</v></c>`;
  }
  return `<c r="${ref}" s="${styleId}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

const safeSheetName = (name, index) =>
  (String(name || `Sheet${index + 1}`).replace(/[\\/?*[\]:]/g, " ").trim() || `Sheet${index + 1}`).slice(0, 31);

function sheetXml({ columns, rows, title }) {
  const headerOffset = title ? 2 : 0;
  const widths = columns
    .map((c, i) => {
      const longest = rows.reduce((max, row) => {
        const text = String(readCell(row, c) ?? "");
        return Math.max(max, text.length);
      }, String(c.label || c.key).length);
      return `<col min="${i + 1}" max="${i + 1}" width="${c.width || Math.min(44, Math.max(11, longest + 3))}" customWidth="1"/>`;
    })
    .join("");

  const lines = [];

  if (title) {
    lines.push(
      `<row r="1" ht="20" customHeight="1">${cellXml("A1", title, 4)}</row>`,
      `<row r="2"/>`
    );
  }

  const headerRow = headerOffset + 1;
  lines.push(
    `<row r="${headerRow}" ht="19" customHeight="1">${columns
      .map((c, i) => cellXml(`${columnLetter(i)}${headerRow}`, c.label || c.key, 1))
      .join("")}</row>`
  );

  rows.forEach((row, rowIndex) => {
    const r = headerOffset + 2 + rowIndex;
    lines.push(
      `<row r="${r}">${columns
        .map((c, i) => {
          const value = readCell(row, c);
          return cellXml(`${columnLetter(i)}${r}`, value, isNumeric(value) ? 3 : 2);
        })
        .join("")}</row>`
    );
  });

  const lastRef = `${columnLetter(Math.max(0, columns.length - 1))}${headerOffset + 1 + rows.length}`;

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetPr><outlinePr summaryBelow="1" summaryRight="1"/></sheetPr>
<dimension ref="A1:${lastRef}"/>
<sheetViews><sheetView workbookViewId="0"><pane ySplit="${headerRow}" topLeftCell="A${headerRow + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
<cols>${widths}</cols>
<sheetData>${lines.join("")}</sheetData>
${rows.length ? `<autoFilter ref="A${headerRow}:${lastRef}"/>` : ""}
<pageMargins left="0.5" right="0.5" top="0.6" bottom="0.6" header="0.3" footer="0.3"/>
</worksheet>`;
}

function readCell(row, column) {
  if (typeof column.value === "function") return column.value(row);
  if (typeof column.render === "function" && row[column.key] === undefined) return "";
  return row[column.key];
}

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="4">
<font><sz val="11"/><name val="Calibri"/><color rgb="FF1F2430"/></font>
<font><b/><sz val="11"/><name val="Calibri"/><color rgb="FFFFFFFF"/></font>
<font><sz val="11"/><name val="Calibri"/><color rgb="FF1F2430"/></font>
<font><b/><sz val="14"/><name val="Calibri"/><color rgb="FF5B4FE0"/></font>
</fonts>
<fills count="3">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FF5B4FE0"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="2">
<border><left/><right/><top/><bottom/><diagonal/></border>
<border><left style="thin"><color rgb="FFE5E7F0"/></left><right style="thin"><color rgb="FFE5E7F0"/></right><top style="thin"><color rgb="FFE5E7F0"/></top><bottom style="thin"><color rgb="FFE5E7F0"/></bottom><diagonal/></border>
</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="5">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="2" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1" applyAlignment="1"><alignment vertical="center"/></xf>
<xf numFmtId="0" fontId="2" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment vertical="center"/></xf>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
<dxfs count="0"/>
<tableStyles count="0" defaultTableStyle="TableStyleMedium9" defaultPivotStyle="PivotStyleLight16"/>
</styleSheet>`;

export function buildXlsxBlob(sheets) {
  const list = sheets
    .filter(Boolean)
    .map((sheet, index) => ({
      ...sheet,
      name: safeSheetName(sheet.name, index),
      columns: sheet.columns || [],
      rows: sheet.rows || [],
    }));

  const files = [
    {
      name: "[Content_Types].xml",
      data: encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${list.map((s, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join("")}
</Types>`),
    },
    {
      name: "_rels/.rels",
      data: encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`),
    },
    {
      name: "xl/workbook.xml",
      data: encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${list.map((s, i) => `<sheet name="${escapeXml(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join("")}</sheets>
</workbook>`),
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      data: encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${list.map((s, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join("")}
<Relationship Id="rId${list.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    },
    { name: "xl/styles.xml", data: encode(STYLES_XML) },
    ...list.map((sheet, i) => ({
      name: `xl/worksheets/sheet${i + 1}.xml`,
      data: encode(sheetXml(sheet)),
    })),
  ];

  return new Blob([zip(files)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function downloadXlsx(filename, sheetsOrColumns, maybeRows) {
  const sheets = Array.isArray(maybeRows)
    ? [{ name: "Sheet1", columns: sheetsOrColumns, rows: maybeRows }]
    : sheetsOrColumns;
  const safeName = String(filename).replace(/[\\/:*?"<>|]/g, "-");
  saveBlob(buildXlsxBlob(sheets), safeName.endsWith(".xlsx") ? safeName : `${safeName}.xlsx`);
}

export default downloadXlsx;
